import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ApplicationSettingEnum, CarbonCopyInput, CustomerDropdownResult, CustomerResult, EmailResult, GetCustomersInput, GetTicketTypesInput, Priority, ProjectResult, Result, SecurityLevelEnum, TicketDocumentInput, TicketInput, TicketTypeResult } from 'app/models';
import { CustomerService, CustomerEmployeeService, EmployeeService, ProjectService, TicketService, TicketTypeService, ApplicationSettingService } from 'app/services';
import { Utility } from 'app/shared';

@Component({
  selector: 'app-save-ticket',
  templateUrl: './save-ticket.component.html',
  styleUrls: ['./save-ticket.component.scss']
})
export class SaveTicketComponent implements OnInit {
  public result: Result = new Result();

  public priority = Priority;
  public utility = Utility;

  public token: string;
  public titleText: string;
  public submitButtonText: string;

  public isBusy: boolean;

  public ticketInput: TicketInput;

  public deleteData = new TicketDocumentInput();

  public customerId: number | null;
  public headerId: number | null;

  public titleIsInvalid: boolean = false;
  public commentIsInvalid: boolean = false;
  public priorityIsInvalid: boolean = false;
  public customerProjectIdIsInvalid: boolean = false;

  public emails: Array<EmailResult>;
  public carbonCopies: Array<EmailResult>;
  public blindCarbonCopies: Array<EmailResult>;

  public carbonCopyIsInvalid: boolean;

  public customerList: Array<CustomerDropdownResult>;
  public projectList: Array<ProjectResult>;
  public ticketTypeList: Array<TicketTypeResult>;
  public applicationSettingEnum = ApplicationSettingEnum;

  constructor(private router: Router,
    private activatedRoute: ActivatedRoute,
    private customerService: CustomerService,
    private customerEmployeeService: CustomerEmployeeService,
    private ticketService: TicketService,
    private ticketTypeService: TicketTypeService,
    private projectService: ProjectService,
    private employeeService: EmployeeService,
    private modalService: NgbModal,
    private applicationSettingService: ApplicationSettingService) {
    this.isBusy = false;
    this.titleText = 'AddTicket';
    this.submitButtonText = 'Add';
    this.customerId = null;
    this.headerId = null;
    this.carbonCopyIsInvalid = false;
    this.ticketInput = new TicketInput();
    this.ticketInput.addTicketCommentInput.isPublic = true;
    this.customerList = new Array<CustomerDropdownResult>();
    this.projectList = new Array<ProjectResult>();
    this.ticketTypeList = new Array<TicketTypeResult>();
    this.emails = new Array<EmailResult>();
    this.carbonCopies = new Array<EmailResult>();
    this.blindCarbonCopies = new Array<EmailResult>();
    this.token = Utility.getTokenType();
    this.resetIsInvalidVariables();
  }

  ngOnInit(): void {
    if (this.token === 'dashboardToken') {
      this.employeeService.checkPermission().then(permissionResult => {
        if (permissionResult.success != true)
          this.router.navigate(['../login'], { relativeTo: this.activatedRoute });
        else if ((Utility.isAccessable(permissionResult.data.securityLevel, SecurityLevelEnum.AddTicket) == false &&
          Utility.isAccessable(permissionResult.data.securityLevel, SecurityLevelEnum.EditTicket) == false) ||
          (Utility.isAccessable(permissionResult.data.securityLevel, SecurityLevelEnum.AddTicket) == true &&
            Utility.isAccessable(permissionResult.data.securityLevel, SecurityLevelEnum.EditTicket) == false &&
            this.activatedRoute.snapshot.params['id'] != undefined) ||
          (Utility.isAccessable(permissionResult.data.securityLevel, SecurityLevelEnum.AddTicket) == false &&
            Utility.isAccessable(permissionResult.data.securityLevel, SecurityLevelEnum.EditTicket) == true &&
            this.activatedRoute.snapshot.params['id'] == undefined))
          this.router.navigate(['../default'], { relativeTo: this.activatedRoute });
        else {
          this.params();
          this.loadFormPreRequirements();
        }
      }).catch(c => {
        this.router.navigate(['../login'], { relativeTo: this.activatedRoute });
      });
    } else {
      this.customerEmployeeService.checkPermission().then(permissionResult => {
        if (permissionResult.success != true)
          this.router.navigate(['../login'], { relativeTo: this.activatedRoute });
        else {
          this.customerId = 0;
          this.loadFormPreRequirements();
          this.getProjectsWithCustomerId();
        }
      }).catch(c => {
        this.router.navigate(['../login'], { relativeTo: this.activatedRoute });
      });

      this.applicationSettingService.getApplicationSettingsHome().then(result => {
        if (result.success === true) {
          const homeOfflineSetting = result.data.find(r => r.key === this.applicationSettingEnum.HomeOffline);
          const homeOffline = homeOfflineSetting ? Boolean(JSON.parse(homeOfflineSetting.value)) : false;

          if (homeOffline) {
            this.router.navigate(['../app-offline'], { relativeTo: this.activatedRoute });
          }
        }
      });

    }
  }

  private params(): void {
    this.activatedRoute.params.forEach(param => {
      if (param.hasOwnProperty('headerId') == true) {
        this.headerId = +param['headerId'];
      }
    });
  }

  private loadFormPreRequirements(): Promise<void> {
    let customers;
    let ticketType;
    if (this.token === 'dashboardToken') {
      const getCustomersInput = new GetCustomersInput();
      getCustomersInput.paginationInformation.currentPage = 0;
      customers = this.customerService.getCustomerdropdown()
        .then(result => {
          if (result.success == true) {
            this.customerList = result.data;
          }
          else
            this.result = result;
        });

      const getTicketTypesInput = new GetTicketTypesInput();
      getTicketTypesInput.paginationInformation.currentPage = 0;
      ticketType = this.ticketTypeService.getTicketTypes(getTicketTypesInput, this.token)
        .then(result => {
          if (result.success == true) {
            this.ticketTypeList = result.data;
          }
          else
            this.result = result;
        });
    }
    return Promise.all([customers, ticketType]).then(() => {
    });
  }

  private loadEmails(): void {
    this.carbonCopies = new Array<EmailResult>();
    this.customerEmployeeService.getAllEmails(this.customerId ?? 0, this.token)
      .then(result => {
        if (result.success === true) {
          this.emails = result.data;
          this.emails.forEach(r => {
            r.fullNameAndEmail = `${r.userFullName} < ${r.email} >`;
          });
        }
        else
          this.result = result;
      });
  }

  public getProjectsWithCustomerId(): void {
    this.loadEmails();
    this.ticketInput.customerProjectId = null;
    this.projectList = new Array<ProjectResult>();
    if (this.customerId !== null) {
      this.projectService.getProjectsWithCustomerId(this.customerId, this.token)
        .then(result => {
          if (result.success == true) {
            this.projectList = result.data;
          }
          else
            this.result = result;
        });
    }
  }

  public displayDeleteModal(modalDanger: any, data: TicketDocumentInput): void {
    this.deleteData = data;
    this.modalService.open(modalDanger, {
      centered: true,
      windowClass: 'modal modal-danger'
    });
  }

  public delete(): void {
    this.ticketInput.addTicketCommentInput.ticketDocuments = this.ticketInput.addTicketCommentInput.ticketDocuments.filter(item => item !== this.deleteData);
    this.modalService.dismissAll();
  }

  // public addNewCarbonCopy(email): any {
  //   const temp = new EmailResult();
  //   temp.id = 0;
  //   temp.email = email;
  //   temp.fullNameAndEmail = email;
  //   temp.userId = null;
  //   temp.userMode = null;
  //   return temp;
  // }

  public submit(): void {
    if (this.validation() == true) {
      this.add();
    }
  }

  private validation(): boolean {
    let isValid = true;
    this.result = new Result();
    this.result.message = '';

    this.resetIsInvalidVariables();

    if (this.token === 'dashboardToken' && this.carbonCopies.length === 0) {
      isValid = false;
      this.carbonCopyIsInvalid = true;
    }

    if (this.ticketInput.addTicketCommentInput.comment === null || this.ticketInput.addTicketCommentInput.comment.trim() === '') {
      isValid = false;
      this.commentIsInvalid = true;
    }

    if (this.ticketInput.title === null || this.ticketInput.title.trim() === '') {
      isValid = false;
      this.titleIsInvalid = true;
    }

    if (this.ticketInput.priority === null) {
      isValid = false;
      this.priorityIsInvalid = true;
    }

    if (this.ticketInput.customerProjectId === null) {
      isValid = false;
      this.customerProjectIdIsInvalid = true;
    }

    if (isValid == false) {
      this.result.success = false;
      this.result.message = 'PleaseCompleteAllRequiredFields';
    }
    return isValid;
  }

  private add(): void {
    if (this.isBusy == false) {
      this.isBusy = true;
      this.carbonCopies.filter(r => Utility.validateEmail(r.email) === true).map(x => {
        const temp = new CarbonCopyInput();
        temp.email = x.email;
        temp.userId = x.userId;
        temp.isBlindCarbonCopy = false;
        temp.userMode = x.userMode;
        this.ticketInput.addTicketCommentInput.carbonCopies.push(temp);
      });
      this.blindCarbonCopies.filter(r => Utility.validateEmail(r.email) === true).map(x => {
        const temp = new CarbonCopyInput();
        temp.email = x.email;
        temp.userId = x.userId;
        temp.isBlindCarbonCopy = true;
        temp.userMode = x.userMode;
        this.ticketInput.addTicketCommentInput.carbonCopies.push(temp);
      });
      this.ticketInput.headerId = this.headerId;
      this.ticketService.addTicket(this.ticketInput, this.token).then(
        result => {
          this.isBusy = false;
          this.result = result;
          if (this.result.success == true) {
            this.result.message = Utility.AddOperationMessage;
            this.resetPage();
          }
        }).catch(() => {
          this.isBusy = false;
        });
    }
  }

  public openFileUploader(): void {
    document.getElementById('selectedFile')?.click();
  }

  public upload(files: any): void {
    this.result = new Result();
    this.result.message = '';

    const fileSize = files[0].size / 1024 / 1024;

    if (fileSize > 3) {
      this.result.message = Utility.FileUploadErrorMessage;
      this.result.success = false;
    }
    else {
      const fileReader = new FileReader();
      const temp = new TicketDocumentInput();

      fileReader.onload = (event: any) => {
        temp.documentFile = new Uint8Array(event.target.result).toString();
      }
      fileReader.readAsArrayBuffer(files[0]);
      temp.documentFileName = files[0].name;
      this.ticketInput.addTicketCommentInput.ticketDocuments.push(temp);
    }
  }

  private resetPage(): void {
    this.customerId = null;
    this.ticketInput = new TicketInput();
    this.ticketInput.addTicketCommentInput.isPublic = true;
    this.carbonCopies = new Array<EmailResult>();
    this.blindCarbonCopies = new Array<EmailResult>();
  }

  private resetIsInvalidVariables(): void {
    this.titleIsInvalid = false;
    this.commentIsInvalid = false;
    this.priorityIsInvalid = false;
    this.carbonCopyIsInvalid = false;
    this.customerProjectIdIsInvalid = false;
  }
}
